import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CalendarCheck, Clock, Loader2 } from "lucide-react";
import { bookSlot, bookSlotAsClient, getOpenSlots } from "@/lib/booking.functions";
import { formatSlotFull, formatSlotTime, groupByLocalDay, type Slot } from "@/lib/booking";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Props {
  /** Signed-in booking path (client portal) vs. public booking path (/book). */
  authenticated?: boolean;
  defaultName?: string;
  defaultEmail?: string;
  onBooked?: () => void;
  compact?: boolean;
}

export function SlotPicker({ authenticated, defaultName, defaultEmail, onBooked, compact }: Props) {
  const fetchSlots = useServerFn(getOpenSlots);
  const book = useServerFn(bookSlot);
  const bookAuthed = useServerFn(bookSlotAsClient);

  const tz = useMemo(
    () => (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC"),
    [],
  );

  const [selected, setSelected] = useState<Slot | null>(null);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [name, setName] = useState(defaultName ?? "");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState<string | null>(null);

  const slotsQuery = useQuery({
    queryKey: ["open-slots"],
    queryFn: () => fetchSlots({ data: {} }),
    staleTime: 30_000,
  });

  const days = useMemo(() => groupByLocalDay(slotsQuery.data?.slots ?? [], tz), [slotsQuery.data, tz]);
  const activeDay = days.find((d) => d.key === dayKey) ?? days[0];

  const mutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("Choose a time first.");
      const payload = {
        slotStart: selected.startsAt,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        notes: notes.trim() || null,
        timezone: tz,
      };
      return authenticated ? bookAuthed({ data: payload }) : book({ data: payload });
    },
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(res.message ?? "We couldn't book that time.");
        slotsQuery.refetch();
        setSelected(null);
        return;
      }
      setConfirmed(res.startsAt!);
      onBooked?.();
      slotsQuery.refetch();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Something went wrong."),
  });

  if (confirmed) {
    return (
      <div className="rounded-[4px] border border-bronze-600/30 bg-white p-8 text-center shadow-soft">
        <CalendarCheck className="mx-auto h-8 w-8 text-bronze-600" />
        <h3 className="mt-4 font-display text-[24px] text-navy-800">You're on the calendar.</h3>
        <p className="mt-2 font-sans text-[15px] text-charcoal-900">{formatSlotFull(confirmed, tz)}</p>
        <p className="mt-3 font-sans text-[13px] text-charcoal-500">
          A confirmation is on its way. If it doesn't arrive, your time is still reserved —
          reach us at currencythoughtsllc@gmail.com.
        </p>
      </div>
    );
  }

  if (slotsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 font-sans text-[14px] text-charcoal-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading open times…
      </div>
    );
  }

  if (!days.length) {
    return (
      <div className="rounded-[4px] border border-bronze-600/25 bg-white p-8 text-center">
        <p className="font-sans text-[15px] text-charcoal-900">
          No open times are published right now.
        </p>
        <p className="mt-2 font-sans text-[13px] text-charcoal-500">
          Send us a note at currencythoughtsllc@gmail.com and we'll arrange a time directly.
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "rounded-[4px] border border-bronze-600/20 bg-white p-6 shadow-soft sm:p-8"}>
      <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-600">
        Choose a day
      </p>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
        {days.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => { setDayKey(d.key); setSelected(null); }}
            className={`shrink-0 rounded-[3px] border px-4 py-2 font-sans text-[13px] transition-colors ${
              activeDay?.key === d.key
                ? "border-bronze-600 bg-navy-800 text-cream-50"
                : "border-bronze-600/30 bg-cream-50 text-navy-800 hover:border-bronze-600"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <p className="mt-6 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-600">
        Choose a time
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {activeDay?.slots.map((s) => (
          <button
            key={s.startsAt}
            type="button"
            onClick={() => setSelected(s)}
            className={`rounded-[3px] border px-3 py-2 font-sans text-[13px] transition-colors ${
              selected?.startsAt === s.startsAt
                ? "border-bronze-600 bg-bronze-600 text-navy-900"
                : "border-bronze-600/30 bg-cream-50 text-navy-800 hover:border-bronze-600"
            }`}
          >
            {formatSlotTime(s.startsAt, tz)}
          </button>
        ))}
      </div>
      <p className="mt-3 flex items-center gap-1.5 font-sans text-[12px] text-charcoal-500">
        <Clock className="h-3.5 w-3.5" /> Times shown in your timezone ({tz}).
      </p>

      {selected && (
        <form
          className="mt-8 border-t border-bronze-600/20 pt-6"
          onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
        >
          <p className="font-sans text-[14px] text-navy-800">
            <span className="font-semibold">Selected:</span> {formatSlotFull(selected.startsAt, tz)}
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="sp-name">Full name</Label>
              <Input id="sp-name" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
            <div>
              <Label htmlFor="sp-email">Email</Label>
              <Input id="sp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                autoCapitalize="none" autoCorrect="off" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="sp-phone">Phone (optional)</Label>
              <Input id="sp-phone" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="sp-notes">What would you like to cover? (optional)</Label>
              <Textarea id="sp-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
            </div>
          </div>
          <div className="mt-6">
            <PrimaryButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Reserving…" : "Confirm this time"}
            </PrimaryButton>
          </div>
          <p className="mt-4 font-sans text-[11px] leading-relaxed text-charcoal-500">
            This is an educational conversation. Nothing discussed constitutes legal, tax, or
            investment advice, and no coverage is bound at this meeting.
          </p>
        </form>
      )}
    </div>
  );
}
